import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { User } from '../models/user';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { principalRouteNavigate } from '../../../shared/utilities/const/route';
import { RuntimeConfigService } from '../../../core/runtime-config.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private config = inject(RuntimeConfigService);
  _hasLoggedOnce = false;
  set hasLoggedOnce(value: boolean) {
    this._hasLoggedOnce = value;
    sessionStorage.setItem('hasLoggedOnce', String(value));
  }
  get hasLoggedOnce(): boolean {
    if (this._hasLoggedOnce) {
      return true;
    }
    const storedValue = sessionStorage.getItem('hasLoggedOnce');
    return storedValue === 'true';
  }
  private appBaseUrl(): URL {
    return new URL(document.baseURI);
  }

  private appUrl(path: string): string {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return new URL(normalizedPath, this.appBaseUrl()).toString();
  }

  private appRootUrl(): string {
    return this.appBaseUrl().toString().replace(/\/$/, '');
  }

  userMe(): Observable<User> {
    return this.http.get<User>(`${environment.urlBE}user/me`);
  }

  refreshSession(): Observable<any> {
    return this.http.get(`${environment.urlBE}auth/refresh`);
  }

  login(idp: string, redirect?: string) {
    const callbackUrl = new URL('/api/auth/idp-callback', window.location.origin).toString();
    const stateUrl = redirect ?? this.appUrl(principalRouteNavigate.verbalList);
    const loginParams = new URLSearchParams({
      action: 'login',
      client_id: this.config.get('SAML_CLIENT_ID'),
      redirect_uri: callbackUrl,
      idp: idp,
      state: stateUrl,
    });
    window.location.assign(this.config.get('BASE_SAML_PROXY_URL') + loginParams.toString());
  }

  oidcLogin(type: 'spid' | 'cie', redirectUrl?: string) {
    const targetRedirectUrl = redirectUrl ?? this.appUrl(principalRouteNavigate.verbalList);
    window.location.assign(
      `${environment.urlBE}auth/login?` +
        new URLSearchParams({ type, redirectUrl: targetRedirectUrl }).toString(),
    );
  }

  /**
   * AUTH_PROVIDER=oidc-client flow: the CSRF state is held in a single
   * non-HttpOnly host-only cookie (`spid_state`, SameSite=None; Secure) so it
   * survives the cross-site POST from the IDP back to the BE. We deliberately
   * avoid duplicating the value in sessionStorage: with XSS in this origin
   * both stores are equally reachable, so the second copy adds attack surface
   * without adding any defensive value.
   */
  oidcClientLogin(type: 'spid' | 'cie', redirectUrl?: string) {
    const targetRedirectUrl = redirectUrl ?? this.appUrl(principalRouteNavigate.verbalList);
    const authority = this.config.get('OIDC_AUTHORITY');
    const target = this.config.get('OIDC_TARGET_URL');
    const level = this.config.get('OIDC_LEVEL');
    if (!authority || !target) {
      throw new Error('Configurazione OIDC mancante (OIDC_AUTHORITY, OIDC_TARGET_URL)');
    }
    const state = this.generateState();
    // Host-only (no Domain attribute), short-lived, SameSite=None;Secure so
    // the cookie reaches the BE callback after the IDP cross-site POST.
    document.cookie = `spid_state=${encodeURIComponent(state)}; Path=/; Max-Age=600; SameSite=None; Secure`;
    document.cookie = `redirect_to=${encodeURIComponent(targetRedirectUrl)}; Path=/; Max-Age=600; SameSite=None; Secure`;
    const loginParams = new URLSearchParams({
      target,
      state,
      level: String(level),
      type,
      autoshow: type,
    });
    window.location.assign(`${authority.replace(/\/$/, '')}/?${loginParams.toString()}`);
  }

  private generateState(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  logout(explicit = true) {
    if (explicit) {
      this.hasLoggedOnce = false;
      window.location.assign(
        `${environment.urlBE}auth/logout?` +
          new URLSearchParams({
            state: this.appRootUrl(),
          }).toString(),
      );
      return;
    }
  }
}
